// M45a — Referral-code helpers. Wraps the SECURITY DEFINER RPCs from
// the 20260523200000_referral_invite_codes.sql migration so the Settings
// card and quick-access modal (M45b) call typed functions instead of
// raw supabase.rpc(). Paired with `./admin.ts`, which holds the admin
// surface (admin_set_user_referral_quota, admin_decide_quota_request,
// admin_list_quota_requests) used by /admin/invites in M45c.
//
// Tracking issue: #70 (umbrella). Phase issue: #72.
//
// User-facing wrappers (this file) keep their own friendly-message
// helper rather than depending on `inviteCodes.ts`'s friendlyInviteError —
// the failure modes differ (no "expired" or "invalid" cases here; we
// have "pending already" and validation bounds instead).
import { supabase } from './supabase';

/** A user's referral code row. Mirrors the shape returned by the
 *  `get_or_create_my_referral_code()` RPC — the user's invite link
 *  (built from `code` via `buildReferralLink`), the live per-user
 *  quota (referral_quota), and how many of those have been redeemed
 *  (uses_count). max_uses on the underlying alpha_invite_codes row is
 *  a clamped mirror of referral_quota (>= uses_count, >= 1); UI should
 *  display "remaining" from referral_quota - uses_count, not from
 *  max_uses, because the two can diverge when the maintainer lowers
 *  a quota after invites have already shipped. */
export interface ReferralCode {
  id: string;
  code: string;
  max_uses: number;
  uses_count: number;
  is_active: boolean;
  archived_at: string | null;
  created_at: string;
  referrer_user_id: string;
  referral_quota: number;
}

/** A "who joined from my link" row returned by `my_invite_redemptions()`.
 *  display_name and account_created_at are null for a redemption row
 *  whose signup never completed (rare — the signup form calls the
 *  redemption RPC right before supabase.auth.signUp, so a redeemed-
 *  but-no-account row only happens if signup itself failed). */
export interface MyInviteRedemption {
  id: string;
  email: string;
  redeemed_at: string;
  display_name: string | null;
  account_created_at: string | null;
}

/** The status values stored on `referral_quota_requests.status`. */
export type QuotaRequestStatus = 'pending' | 'approved' | 'denied' | 'cancelled';

/** A row from the caller's own `referral_quota_requests`. The Settings
 *  card uses the most recent row to render the pending-state UI; the
 *  full history isn't exposed to users in v1 (admin sees it via M45c). */
export interface MyQuotaRequest {
  id: string;
  user_id: string;
  reason: string | null;
  requested_amount: number | null;
  status: QuotaRequestStatus;
  granted_amount: number | null;
  decided_by: string | null;
  decided_at: string | null;
  decided_note: string | null;
  created_at: string;
}

/** Friendly errors thrown by the wrappers; distinguishable from
 *  network/Postgres errors via instanceof. Callers in the Settings
 *  card catch this to surface the message directly in a Snackbar. */
export class ReferralError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReferralError';
  }
}

/** Return the caller's referral code, creating one on first call.
 *  Idempotent — safe to call on every Settings render / modal open.
 *  Throws ReferralError on any RPC failure (auth, network, schema). */
export async function getOrCreateMyReferralCode(): Promise<ReferralCode> {
  const { data, error } = await supabase.rpc('get_or_create_my_referral_code');
  if (error) {
    throw new ReferralError(`Failed to load referral code: ${error.message}`);
  }
  const rows = (data ?? []) as ReferralCode[];
  if (rows.length === 0) {
    // The RPC creates-then-returns, so an empty result is unexpected;
    // surface a clear message instead of returning a sentinel.
    throw new ReferralError('Referral-code lookup returned no rows.');
  }
  return rows[0];
}

/** Effective "X of Y remaining" — Y is the user's quota, X is how many
 *  invites haven't been redeemed yet. Floored at 0 in case the
 *  maintainer lowered the quota below uses_count after redemptions
 *  landed (the underlying code's max_uses can't shrink below
 *  uses_count, but referral_quota can). Pure — deterministic. */
export function referralRemaining(
  c: Pick<ReferralCode, 'referral_quota' | 'uses_count'>,
): number {
  return Math.max(c.referral_quota - c.uses_count, 0);
}

/** Submit a quota-increase request. `requestedAmount` is optional —
 *  null means "let the maintainer decide." Throws ReferralError with
 *  a friendly message on validation failure or a pending-already
 *  conflict. Returns the new request's UUID on success. */
export async function requestReferralQuotaIncrease(input: {
  reason?: string | null;
  requestedAmount?: number | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('request_referral_quota_increase', {
    p_reason: input.reason ?? null,
    p_requested_amount: input.requestedAmount ?? null,
  });
  if (error) throw new ReferralError(friendlyRequestError(error.message));
  return (data as string | null) ?? '';
}

/** Cancel the caller's own pending request. Server-side check confirms
 *  ownership + pending status; a no-op cancel surfaces as a friendly
 *  error so the UI can render "nothing to cancel." */
export async function cancelReferralQuotaRequest(id: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_referral_quota_request', {
    p_id: id,
  });
  if (error) throw new ReferralError(friendlyRequestError(error.message));
}

/** "Who invited me" pointer for the current user. Returns the
 *  referrer's uuid + display_name if `profiles.referred_by_user_id` is
 *  set, or `{ referrer_user_id: null, referrer_display_name: null }`
 *  if the caller wasn't invited by another alpha user (admin-code
 *  signup, or a pre-referral-feature account). The Settings card uses
 *  this to render an "Invited by: <name>" line — hidden when the
 *  referrer is null. Returns null only when the caller has no profile
 *  row at all (shouldn't happen for an authenticated user). */
export async function getMyReferrerInfo(): Promise<{
  referrer_user_id: string | null;
  referrer_display_name: string | null;
} | null> {
  const { data, error } = await supabase.rpc('get_my_referrer_info');
  if (error) {
    throw new ReferralError(`Failed to load referrer info: ${error.message}`);
  }
  const rows = (data ?? []) as Array<{
    referrer_user_id: string | null;
    referrer_display_name: string | null;
  }>;
  return rows[0] ?? null;
}

/** Who joined from the caller's referral link, newest first. */
export async function listMyInviteRedemptions(): Promise<MyInviteRedemption[]> {
  const { data, error } = await supabase.rpc('my_invite_redemptions');
  if (error) {
    throw new ReferralError(`Failed to load redemptions: ${error.message}`);
  }
  return (data ?? []) as MyInviteRedemption[];
}

/** Fetch the caller's most recent referral_quota_request (any state),
 *  or null if they've never filed one. The Settings card uses this to
 *  decide whether to render the pending badge + cancel link or the
 *  "Request more" button. RLS on `referral_quota_requests` limits
 *  visibility to the caller's own rows, so a plain `from(...)` select
 *  is safe (no need for a dedicated RPC). */
export async function getMyLatestQuotaRequest(): Promise<MyQuotaRequest | null> {
  const { data, error } = await supabase
    .from('referral_quota_requests')
    .select(
      'id,user_id,reason,requested_amount,status,granted_amount,decided_by,decided_at,decided_note,created_at',
    )
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    throw new ReferralError(`Failed to load quota request: ${error.message}`);
  }
  return (data as MyQuotaRequest | null) ?? null;
}

/** Build the shareable URL for a referral code. Centralized so the
 *  Settings card, quick-access modal, and any future surface stay in
 *  sync. Uses window.location.origin so it works in dev (localhost),
 *  preview deploys, and prod (quarterstack.app) without per-env
 *  configuration. Falls back to the prod origin when called outside a
 *  browser (SSR / Node test) — there's no browser context in those
 *  cases, and the link prefix needs to be a usable URL regardless. */
export function buildReferralLink(code: string): string {
  const base =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'https://quarterstack.app';
  return `${base}/register?invite=${encodeURIComponent(code)}`;
}

/** Translate a raw Postgres error message from one of the user RPCs
 *  into a Snackbar-ready string. Falls back to a generic message for
 *  unknown shapes so we never bubble up `errcode P0003` to the UI. */
function friendlyRequestError(message: string | undefined | null): string {
  if (!message) return 'We could not save that request. Please try again.';
  const m = message.toLowerCase();
  if (m.includes('not authenticated')) {
    return 'You need to be signed in to do that.';
  }
  if (m.includes('between 1 and 10')) {
    return 'Pick a requested amount between 1 and 10.';
  }
  if (m.includes('exceeds 280 characters')) {
    return 'The reason is too long — keep it under 280 characters.';
  }
  if (m.includes('already have a pending request')) {
    return 'You already have a pending request. Cancel it before submitting another.';
  }
  if (m.includes('no pending request to cancel')) {
    return 'There is no pending request to cancel.';
  }
  return 'We could not save that request. Please try again.';
}
